import { ApiResponse, ApisauceConfig, create } from 'apisauce';

import { UserBase } from '../types/user';
import { UserInput } from '../types/registration/userInput';
import {
  BuildingDetails,
  BuildingSearchResponse,
  DetailedBuildingDetails,
  ExpertReport,
  Property,
  Registration,
  RegistrationRequest,
  SearchValues,
  SimpleEnergyAnalysis
} from '../types/building';
import { CalculationParameterMap } from '../types/calculationParameters';
import { XMLReport } from '../types/expertToolsXML';
import { EnovaStotteCriteria } from '../types/soknad';
import { Tiltak, Tiltakskategori } from '../types/registration/tiltak';
import {
  CreatePortefoljeDTO,
  Portefolje,
  PortefoljeEnergiAnalyseResponse
} from '../screens/expertToolsXMLPortfolio/types';
import { BygningsForm, Malform, SoknadSteg } from '../components/registering/utils/registerEnums';
import { InsulationData } from '../types/misc';
import { EnergiObjektType } from '../utils/matrikkelBygningsTyper';
import { authManager } from '../auth';
import { TiltaksListe } from '../types/expertToolsJSON';

import {
  BrukersEkspertRegistreringerResponse,
  CreateBuildingPayload,
  EkspertRegistrationResponse,
  FlerboligRegistrationResponse,
  FlerboligResponse,
  NavnSokResponse,
  NummerSokResponse,
  PublishRegistrationDTO,
  SearchOptions,
  SubmitRegistrationDTO,
  UpdateRegistrationDTO,
  XMLResponse
} from './types';
import { getFilename, searchCompanyName, searchOrganizationNumber } from './utils';
import { RequestTrace, getRequestTrace, traceApiRequests } from './requestTracing';

const apiBaseURL = window.config.VITE_API_URL;

/**
 * Portal API ProblemDetails `errorCode` strings.
 * Align with `Enova.Ems.Common.PortalProblemDetailsErrorCodes` (backend).
 * Solar strings mirror `Enova.Ems.Engine.Energimerke.Interface.SolarErrorCodes`.
 *
 * For these codes, user-visible copy is defined in locale files (`nb-NO.json`, `nn-NO.json`); API `title`/`detail` are English for operators and tooling.
 */
export const PortalProblemDetailsErrorCodes = {
  RegistrationUnavailable: 'REGISTRATION_UNAVAILABLE',
  EnergimerkeCalculationFailed: 'ENERGIMERKE_CALCULATION_FAILED',
  SolarExternalServiceUnavailable: 'SOLAR_EXTERNAL_SERVICE_UNAVAILABLE',
  SolarCalculationFailed: 'SOLAR_CALCULATION_FAILED'
} as const;

export const SolarExternalServiceUnavailableErrorCode = PortalProblemDetailsErrorCodes.SolarExternalServiceUnavailable;
export const SolarCalculationFailedErrorCode = PortalProblemDetailsErrorCodes.SolarCalculationFailed;

type ProblemDetailsPayload = {
  status?: number;
  title?: string;
  detail?: string;
  traceId?: string;
  errorCode?: string;
};

export type ApiRequestErrorContext = {
  registrationId?: string;
  route?: string;
  step?: string;
};

export class ApiRequestError extends Error {
  status?: number;
  detail?: string;
  traceId?: string;
  errorCode?: string;
  problem?: string;
  context?: ApiRequestErrorContext;
  // The trace the failed request was sent with, so its Sentry event links to the request's trace.
  requestTrace?: RequestTrace;

  constructor(message: string, details?: ProblemDetailsPayload & { problem?: string; requestTrace?: RequestTrace }) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = details?.status;
    this.detail = details?.detail;
    this.traceId = details?.traceId;
    this.errorCode = details?.errorCode;
    this.problem = details?.problem;
    this.requestTrace = details?.requestTrace;
  }
}

// Adds runtime context before the error reaches shared logging handlers.
export const attachApiRequestErrorContext = (
  error: ApiRequestError,
  context: ApiRequestErrorContext
): ApiRequestError => {
  error.context = {
    ...error.context,
    ...context
  };

  return error;
};

// Wraps errors caught outside api.ts (where no ApiResponse is available) so callers can always
// handle an ApiRequestError. No traceId: such an error belongs to no API request, and any other
// trace id (e.g. the page's) is not in LogAnalysis, so it would only mislead in support details.
export const toApiRequestError = (error: unknown, fallbackMessage: string): ApiRequestError =>
  error instanceof ApiRequestError
    ? error
    : new ApiRequestError(fallbackMessage);

// Support details are copied as plain text so they can be pasted into tickets or chat.
export const getRegistrationSupportDetails = (error: ApiRequestError | null, registrationId?: string) => {
  const details = [
    error?.traceId ? `traceId: ${error.traceId}` : undefined,
    registrationId ? `registrationId: ${registrationId}` : undefined,
    error?.context?.step ? `step: ${error.context.step}` : undefined,
    error?.context?.route ? `route: ${error.context.route}` : undefined,
    error?.errorCode ? `errorCode: ${error.errorCode}` : undefined,
    error?.status ? `status: ${error.status}` : undefined
  ].filter((detail): detail is string => !!detail);

  return {
    traceId: error?.traceId,
    supportDetails: details.join('\n')
  };
};

const toProblemDetailsPayload = (data: unknown): ProblemDetailsPayload | undefined => {
  if (!data || typeof data !== 'object') {
    return undefined;
  }

  const payload = data as Record<string, unknown>;
  return {
    status: typeof payload.status === 'number' ? payload.status : undefined,
    title: typeof payload.title === 'string' ? payload.title : undefined,
    detail: typeof payload.detail === 'string' ? payload.detail : undefined,
    traceId: typeof payload.traceId === 'string' ? payload.traceId : undefined,
    errorCode: typeof payload.errorCode === 'string' ? payload.errorCode : undefined
  };
};

export const getCorrelationIdHeader = (headers?: ApiResponse<unknown>['headers']) => {
  const headerValue = headers?.['x-correlation-id'];

  if (typeof headerValue === 'string' && headerValue.trim()) {
    return headerValue;
  }

  if (Array.isArray(headerValue)) {
    return headerValue.find((value) => typeof value === 'string' && value.trim());
  }

  return undefined;
};

export const createApiRequestError = (response: ApiResponse<unknown>, defaultMessage: string): ApiRequestError => {
  const details = toProblemDetailsPayload(response.data);
  // Every request is sent in its own trace (see `traceApiRequests`), so the trace it carried
  // in `traceparent` is the id shared by Sentry and LogAnalysis.
  const requestTrace = getRequestTrace(response.config);
  // Prefer the API correlation header: it is the trace id the backend actually logged under.
  // Fall back to the request's own trace id so network errors, timeouts and CORS failures —
  // where no backend response is readable — still carry a correlatable id, and only then to
  // JSON `traceId`, where ASP.NET may put a hierarchical request id instead.
  const traceId = getCorrelationIdHeader(response.headers) || requestTrace?.traceId || details?.traceId;
  const textResponse = typeof response.data === 'string' ? response.data : undefined;
  const derivedErrorCode =
    details?.errorCode ||
    (textResponse === PortalProblemDetailsErrorCodes.SolarExternalServiceUnavailable ||
      textResponse === PortalProblemDetailsErrorCodes.SolarCalculationFailed
      ? textResponse
      : undefined);
  const message = details?.detail || details?.title || textResponse || response.problem || defaultMessage;
  return new ApiRequestError(message, {
    ...details,
    traceId,
    errorCode: derivedErrorCode,
    problem: response.problem || undefined,
    requestTrace
  });
};

export const isSolarSubmitErrorCode = (errorCode?: string) =>
  errorCode === PortalProblemDetailsErrorCodes.SolarExternalServiceUnavailable ||
  errorCode === PortalProblemDetailsErrorCodes.SolarCalculationFailed;

export const getSolarSubmitErrorVariant = (errorCode?: string): 'unavailable' | 'failed' => {
  return errorCode === PortalProblemDetailsErrorCodes.SolarExternalServiceUnavailable ? 'unavailable' : 'failed';
};

export const getRegistrationSubmitErrorTextKeys = (errorCode?: string) => {
  if (errorCode === PortalProblemDetailsErrorCodes.RegistrationUnavailable) {
    return {
      titleKey: 'register.submitError.registrationUnavailable.title',
      descriptionKey: 'register.submitError.registrationUnavailable.description'
    };
  }

  if (errorCode === PortalProblemDetailsErrorCodes.EnergimerkeCalculationFailed) {
    return {
      titleKey: 'register.submitError.energimerkeCalculationFailed.title',
      descriptionKey: 'register.submitError.energimerkeCalculationFailed.description'
    };
  }

  if (!isSolarSubmitErrorCode(errorCode)) {
    return {
      titleKey: 'register.summary.error.title',
      descriptionKey: 'register.summary.error.description'
    };
  }

  const solarErrorVariant = getSolarSubmitErrorVariant(errorCode);
  return {
    titleKey: `register.solar.submitError.${solarErrorVariant}.title`,
    descriptionKey: `register.solar.submitError.${solarErrorVariant}.description`
  };
};

const commonConfig: Partial<ApisauceConfig> = {
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json;charset=UTF-8'
  },
  timeout: 30000
};

export const api = create({
  ...commonConfig,
  baseURL: apiBaseURL
});

// Send every request made to the api in its own distributed trace
traceApiRequests(api.axiosInstance);

// Add authorization to every request made to the api
api.addAsyncRequestTransform(async (request) => {
  const user = await authManager.getUser();

  if (request.headers) {
    request.headers['Authorization'] = `Bearer ${user?.access_token}`;
  }
});

const wsGeoNorgeApi = create({
  ...commonConfig,
  baseURL: 'https://ws.geonorge.no'
});

const brregApi = create({
  ...commonConfig,
  baseURL: 'https://data.brreg.no'
});

// GET
export const getInsulationValues = async () => {
  const response = await api.get<InsulationData>('registrering/load');

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve insulation values');
  }

  const data = response.data;

  if (!data) {
    throw new Error('Missing insulation data');
  }

  return data;
};

export const getUser = async () => {
  const response = await api.get<UserBase>('bruker');

  if (!response.ok) {
    const error = attachApiRequestErrorContext(
      createApiRequestError(response, 'Could not retrieve user'),
      { route: 'bruker' }
    );
    // HTTP status is needed for 401/403 handling on the login retry screen.
    // Do not copy response.status in createApiRequestError globally: Sentry treats
    // 401/403/404 as expected and would drop them for every other Portal call.
    error.status = error.status ?? response.status;
    throw error;
  }

  return response.data;
};

export const getBuilding = async (energiobjektId?: string, cancelToken?: ApisauceConfig['cancelToken']) => {
  if (!energiobjektId) {
    throw new Error("Can't get building without energiobjektId");
  }

  const response = await api.get<DetailedBuildingDetails>(
    `registrerteiendom/oppsummering/${energiobjektId}`,
    undefined,
    {
      cancelToken
    }
  );

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve building');
  }

  return response.data;
};

export const getBuildingList = async () => {
  const response = await api.get<BuildingDetails[]>('registrerteiendom/oppsummering');

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve building list');
  }

  return response.data;
};

export const getUserSelectedBuildingList = async (
  cancelToken?: ApisauceConfig['cancelToken'],
  pageSize: number = 2000
) => {
  const response = await api.get<BuildingDetails[]>(`bruker/valgtebygninger?pageSize=${pageSize}`, undefined, {
    cancelToken
  });

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve user selected building list');
  }

  return response.data;
};

export const getSimpleEnergyAnalyses = async (buildingId?: string) => {
  if (!buildingId) {
    throw new Error("Can't get energy analysis without buildingId");
  }

  const response = await api.get<SimpleEnergyAnalysis[]>(`energianalyse/bygg/${buildingId}`, undefined);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve energy analyses');
  }

  return response.data;
};

export const getExpertReports = async (buildingId?: string) => {
  if (!buildingId) {
    throw new Error('Cant retrieve energy reports without buildingId');
  }

  const response = await api.get<ExpertReport[]>(`ekspertrapport/bygg/${buildingId}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve expert reports');
  }

  return response.data;
};

export const getEnergyAnalysis = async (registreringId: string | undefined) => {
  if (!registreringId) {
    throw new Error('Cant retrieve energy analysis when missing registrationId');
  }

  const response = await api.get<SimpleEnergyAnalysis[]>(`energianalyse/${registreringId}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve energy analysis');
  }

  return response.data;
};

export const getUserInput = async (id: string | undefined) => {
  if (!id) {
    throw new Error('Missing id when retrieving userinput');
  }

  const response = await api.get<UserInput>(`userinput/${id}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve user input');
  }

  return response.data;
};

export const getSearchBuilding = async (address: SearchValues, cancelToken?: ApisauceConfig['cancelToken']) => {
  const searchParams = new URLSearchParams();

  Object.entries(address).forEach(([key, value]) => searchParams.append(key, value as string));

  const response = await api.get<BuildingDetails[]>(
    `registrerteiendom/oppsummering/adresse?${searchParams.toString()}&inkluderManueltRegistrerte=true`,
    undefined,
    { cancelToken }
  );

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not search buildings');
  }

  return response.data;
};

export const getUnofficialEnergyCertificate = async (registrationId: string | undefined) => {
  if (!registrationId) {
    throw new Error('Missing registrationId to fetch unofficial energy vertificate');
  }

  const response = await api.get<Blob>(
    `energiattest`,
    { registreringId: registrationId },
    {
      responseType: 'blob',
      headers: { Accept: 'application/pdf' }
    }
  );

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve energy certificate');
  }

  if (!response.data) {
    throw new Error(`Cant find energy certificate for registration id "${registrationId}"`);
  }

  const contentDisposition = response.headers?.['content-disposition'];
  const filename = getFilename(contentDisposition);

  return { data: response.data, filename };
};

export const getUnofficialEnergyCertificateWithPrioritizedMeasures = async (
  registrationId: string | undefined,
  tiltaksListe: TiltaksListe | undefined
) => {
  if (!registrationId) {
    throw new Error('Missing registrationId to fetch unofficial energy vertificate');
  }

  const response = await api.post<Blob>(
    `energiattest/generate`,
    {
      registreringId: registrationId,
      tiltaksListe: tiltaksListe
    },
    {
      responseType: 'blob',
      headers: { Accept: 'application/pdf' }
    }
  );

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve energy certificate with prioritized measures');
  }

  if (!response.data) {
    throw new Error(`Cant find energy certificate for registration id "${registrationId}"`);
  }

  const contentDisposition = response.headers?.['content-disposition'];
  const filename = getFilename(contentDisposition);

  return { data: response.data, filename };
};

export const getUnofficialEnergyCertificateFlerboligbygg = async (flerboligbyggId?: string) => {
  if (!flerboligbyggId) {
    throw new Error('Missing flerboligbyggId to fetch unofficial energy certificate');
  }
  const response = await api.get<Blob>(
    `energiattest`,
    { flerboligbyggId: flerboligbyggId },
    {
      responseType: 'blob',
      headers: { Accept: 'application/pdf' }
    }
  );

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve energy certificate for flerboligbygg');
  }

  if (!response.data) {
    throw new Error(`Cant find energy certificate for registration id "${flerboligbyggId}"`);
  }

  const contentDisposition = response.headers?.['content-disposition'];
  const filename = getFilename(contentDisposition);

  return { data: response.data, filename };
};

export const getUnofficialEnergyCertificateFlerboligbyggWithPrioritizedMeasures = async (
  flerboligbyggId?: string,
  tiltaksListe?: TiltaksListe
) => {
  if (!flerboligbyggId) {
    throw new Error('Missing flerboligbyggId to fetch unofficial energy certificate');
  }
  const response = await api.post<Blob>(
    `energiattest/generate`,
    { flerboligbyggId: flerboligbyggId, tiltaksListe: tiltaksListe },
    {
      responseType: 'blob',
      headers: { Accept: 'application/pdf' }
    }
  );

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve energy certificate for flerboligbygg with prioritized measures');
  }

  if (!response.data) {
    throw new Error(`Cant find energy certificate for registration id "${flerboligbyggId}"`);
  }

  const contentDisposition = response.headers?.['content-disposition'];
  const filename = getFilename(contentDisposition);

  return { data: response.data, filename };
};

export const getSoknadTiltakslisteReport = async (
  soknadId: string,
  soknadSteg: SoknadSteg,
  cancelToken: ApisauceConfig['cancelToken']
) => {
  const response = await api.get<string>(`energiattest/${soknadId}/${soknadSteg}`, undefined, {
    cancelToken
  });

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve soknad report');
  }

  if (!response.data) {
    throw new Error(`Cant find report for application id "${soknadId}" and application step "${soknadSteg}"`);
  }

  return response.data;
};

export const getSolarBalanceCsv = async (registrationId: string | undefined) => {
  if (!registrationId) {
    throw new Error('Missing registrationId to fetch solar balance csv');
  }

  const response = await api.get<Blob>(`energiplan/solceller/csv/${registrationId}`, undefined, {
    responseType: 'blob',
    headers: { Accept: 'text/csv' }
  });

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not generate solar balance csv');
  }

  if (!response.data) {
    throw new Error(`Could not generate solar balance csv for registration id "${registrationId}"`);
  }

  const contentDisposition = response.headers?.['content-disposition'];
  const filename = getFilename(contentDisposition) || `solar-energy-balance-${registrationId}.csv`;

  return { data: response.data, filename };
};

export const getUnpublishedExpertRegistrations = async () => {
  const response = await api.get<BrukersEkspertRegistreringerResponse>(`energianalyse/ekspert`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve unpublished expert registrations');
  }

  return response.data;
};

export const grantCertificateConsent = async (energiplanId: string) => {
  const response = await api.post<string>(`energiattest/consent/grant/`, {
    energiplanId
  });

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not grant certificate consent');
  }

  if (!response.data) {
    throw new Error(`Failed to grant certificate consent for id ${energiplanId}`);
  }

  return response.data;
};

export const revokeCertificateConsent = async (energiplanId: string) => {
  const response = await api.post<string>(`energiattest/consent/revoke/`, {
    energiplanId
  });

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not revoke certificate consent');
  }

  if (!response.data) {
    throw new Error(`Failed to revoke certificate consent for id ${energiplanId}`);
  }

  return response.data;
};

export const getPortefoljer = async (isPublished?: boolean) => {
  const response = await api.get<Portefolje[]>('portefolje/bruker', {
    isPublished
  });

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve portefoljer');
  }

  return response.data || [];
};

export const getPortefoljeData = async (portefoljeId?: string) => {
  if (!portefoljeId) {
    throw new Error("Can't get portefolje data without portefoljeId");
  }

  const response = await api.get<PortefoljeEnergiAnalyseResponse>(`energianalyse/portefolje/${portefoljeId}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve portefolje data');
  }

  return response.data;
};

export const getPortefoljeTiltak = async (portefoljeId: string | undefined) => {
  if (!portefoljeId) {
    throw new Error('Cant retrieve measures without a portefoljeId');
  }

  const response = await api.get<Tiltak[]>(`tiltak/portefolje/${portefoljeId}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve portefolje tiltak');
  }

  return response.data;
};

export const getEnergiplanTiltak = async (energiplanId: string | undefined) => {
  if (!energiplanId) {
    throw new Error('Cant retrieve energyplan measures when missing energiplanId');
  }

  const response = await api.get<Tiltak[]>(`tiltak/energiplan/${energiplanId}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve energiplan tiltak');
  }

  return response.data;
};

export const getEnovaSupportLevels = async (soknadId: string, soknadSteg?: SoknadSteg) => {
  const response = await api.get<EnovaStotteCriteria>(`enovastotte/${soknadId}${soknadSteg ? `/${soknadSteg}` : ''}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve Enova support levels');
  }

  return response.data;
};

// External
export const getSearchAddress = async (searchString?: string, options?: SearchOptions) => {
  if (!searchString) {
    throw new Error('Missing search address');
  }

  const searchParams = new URLSearchParams();

  searchParams.set('sok', searchString);
  searchParams.set('side', options?.page || '0');
  searchParams.set('treffPerSide', options?.hitsPerPage || '15');

  if (options?.fuzzySearch) {
    searchParams.set('fuzzy', 'true');
  }

  const response = await wsGeoNorgeApi.get<BuildingSearchResponse>(`/adresser/v1/sok?${searchParams.toString()}`);

  // Return empty result if address is not found
  if (response.status === 404) {
    return { adresser: [] };
  }

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not search address');
  }

  return response.data;
};

export const getSearchPoststed = async (postnummer: string | null) => {
  if (!postnummer) {
    throw new Error('Missing postnummer');
  }

  const response = await wsGeoNorgeApi.get<BuildingSearchResponse>(
    `adresser/v1/sok?fuzzy=false&postnummer=${postnummer}&filtrer=adresser&treffPerSide=1&side=0`
  );

  // Return empty result if address is not found
  if (response.status === 404) {
    return { adresser: [] };
  }

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve poststed');
  }

  return response.data;
};

export type GeoNorgeMatrikkelSearchCriteria = {
  kommunenummer: string;
  gardsnummer: string | number;
  bruksnummer: string | number;
  adressenavn?: string;
  nummer?: string | number;
  bokstav?: string;
};

type KommuneCenterPointResponse = {
  punktIOmrade?: {
    coordinates?: [number, number];
  };
};

export const searchAddressByMatrikkel = async (criteria: GeoNorgeMatrikkelSearchCriteria) => {
  const params = new URLSearchParams();
  params.set('kommunenummer', criteria.kommunenummer);
  params.set('gardsnummer', String(criteria.gardsnummer));
  params.set('bruksnummer', String(criteria.bruksnummer));
  params.set('treffPerSide', '10');
  params.set('side', '0');
  params.set(
    'filtrer',
    'adresser.adressenavn,adresser.nummer,adresser.bokstav,adresser.kommunenummer,adresser.gardsnummer,adresser.bruksnummer,adresser.representasjonspunkt'
  );

  if (criteria.adressenavn) {
    params.set('adressenavn', criteria.adressenavn);
  }

  if (criteria.nummer !== undefined && criteria.nummer !== null && `${criteria.nummer}`.trim() !== '') {
    params.set('nummer', String(criteria.nummer));
  }

  if (criteria.bokstav) {
    params.set('bokstav', criteria.bokstav);
  }

  const response = await wsGeoNorgeApi.get<BuildingSearchResponse>(`adresser/v1/sok?${params.toString()}`);

  if (response.status === 404) {
    return { adresser: [] };
  }

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not search address by matrikkel');
  }

  return response.data;
};

export const fetchKommuneCenterPoint = async (kommunenummer: string) => {
  if (!kommunenummer) {
    throw new Error('Missing kommunenummer');
  }

  const response = await wsGeoNorgeApi.get<KommuneCenterPointResponse>(`kommuneinfo/v1/kommuner/${kommunenummer}`);

  if (response.status === 404) {
    return undefined;
  }

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not fetch kommune center point');
  }

  const coordinates = response.data?.punktIOmrade?.coordinates;

  if (!coordinates || coordinates.length < 2) {
    return undefined;
  }

  const [longitude, latitude] = coordinates;
  return { latitude, longitude };
};

export const fetchKommuneByNavn = async (kommunenavn: string) => {
  const params = new URLSearchParams();
  params.append('filtrer', 'kommuner.kommunenummer,kommuner.kommunenavnNorsk');
  params.append('knavn', `*${kommunenavn}*`);

  const response = await wsGeoNorgeApi.get<NavnSokResponse>(`kommuneinfo/v1/sok?${params.toString()}`);

  // Return empty result if kommune is not found
  if (response.status === 404) {
    return { kommuner: [] };
  }

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not fetch kommune by navn');
  }

  return response.data;
};

export const fetcKommuneByNummer = async (kommunenummer: string) => {
  const response = await wsGeoNorgeApi.get<NummerSokResponse>(`kommuneinfo/v1/kommuner/${kommunenummer}`);

  // Return empty result if kommune is not found
  if (response.status === 404) {
    return { kommuner: [] };
  }

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not fetch kommune by nummer');
  }

  const data = response.data;

  return {
    kommuner: [
      {
        kommunenummer: data?.kommunenummer,
        kommunenavnNorsk: data?.kommunenavnNorsk
      }
    ]
  };
};

export const searchCompany = async (companyNameOrOrgNumber: string) => {
  // Regex to determine if a string only contains numbers
  const numberRegex = /^\d+$/;

  if (numberRegex.test(companyNameOrOrgNumber) && companyNameOrOrgNumber.length === 9) {
    return await searchOrganizationNumber(companyNameOrOrgNumber, brregApi);
  }

  return await searchCompanyName(companyNameOrOrgNumber, brregApi);
};

// PUT
/**
 * Updates the UserInput object of a given registration
 * @param {string} registrationId The ID of the registration to be updated
 * @param {UserInput} userInput Updated UserInput object
 * @returns {Promise} Promise object specifies whether the update was successful
 */
export const updateRegistration = async (registrationId: string | undefined, userInput: UserInput) => {
  if (!registrationId) {
    throw new Error('Missing registrationId when updating registration');
  }

  const response = await api.put<UpdateRegistrationDTO>(`userinput/${registrationId}`, userInput);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not update registration');
  }

  const data = response.data;

  if (!data?.userInput) {
    throw new Error('Missing data when updating registration');
  }

  return data;
};

export const updateBaseUser = async (user: UserBase) => {
  const response = await api.put<UserBase>('bruker', user);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not update user');
  }

  return response.data;
};

export const updateInputParameters = async (
  registrationId: string | undefined,
  parameters?: CalculationParameterMap
) => {
  if (!registrationId) {
    throw new Error('Missing registrationId when updating parameters');
  }

  const response = await api.put<CalculationParameterMap>(`energimerke/parameter/${registrationId}`, parameters);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not update input parameters');
  }

  return response.data;
};

export const updateRegistreringLanguage = async (registrationId: string | undefined, language: Malform) => {
  if (!registrationId) {
    throw new Error('Cant update language when no registrationId is provided');
  }

  const response = await api.put(`registrering/${registrationId}/sprak/${language}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not update registration language');
  }

  return response.data;
};

export const updateTiltak = async (tiltak: Partial<Tiltak>) => {
  console.log('here ', tiltak);
  const response = await api.put<Tiltak>(`tiltak/${tiltak.tiltakId}`, tiltak);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not update tiltak');
  }

  return response.data;
};
export const getTiltak = async (tiltakId: string) => {
  const response = await api.get<Tiltak[]>(`tiltak/${tiltakId}`);

  if (!response.ok) {
    throw new Error(response.problem);
  }

  return response.data;
};

// POST
export const calculateArea = async (bygningsform: BygningsForm, vegglengder: Array<{ lengde: number; id: string }>) => {
  const response = await api.post<{ areal: number }>('registrering/calculate/area', {
    bygningsform,
    vegglengder
  });

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not calculate area');
  }

  return response.data;
};

export const createBuilding = async (payload: CreateBuildingPayload) => {
  const response = await api.post<string>('eiendom', payload);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not create building');
  }

  const data = response.data;

  if (!data) {
    throw new Error('Missing id from response when creating building');
  }

  return { data, status: response.status };
};

export const createRegistration = async (registrationRequestDTO: RegistrationRequest) => {
  const response = await api.post<Registration>('registrering', registrationRequestDTO);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not create registration');
  }

  const data = response.data;
  const userInput = data?.userInput;

  if (!userInput) {
    throw new Error('Missing reponse data when creating registration');
  }

  return { ...data, userInput };
};

export const archiveAndCloneRegistration = async (registrationId: string, energiplanId: string) => {
  const response = await api.post<Registration>(`energiplan/cloneAndArchive`, {
    registrationId,
    energiplanId
  });

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not archive and clone registration');
  }

  return response.data;
};

export const getFlerboligRegistration = async (flerboligId?: string) => {
  if (!flerboligId) {
    throw new Error('Can not get flerboligregistrering without flerboligId');
  }

  const response = await api.get<FlerboligRegistrationResponse>(`energianalyse/flerboligbygg/${flerboligId}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve flerbolig registration');
  }

  return response.data;
};

export const createExpertRegistration = async (inputJson: XMLReport, bruksenhetsId: string) => {
  const response = await api.post<XMLResponse>('registrering/ekspertbygg', {
    EkspertRegistrering: JSON.stringify(inputJson),
    BruksenhetsId: bruksenhetsId
  });

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not create expert registration');
  }

  return response.data;
};

export const createExpertRegistrationJson = async (inputJson: object, bruksenhetsId: string) => {
  const response = await api.post<EkspertRegistrationResponse>('registrering/ekspertbygg/json', {
    EkspertRegistrering: JSON.stringify(inputJson),
    BruksenhetsId: bruksenhetsId
  });

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not create expert registration');
  }

  return response.data;
};

export const createFlerboligRegistrations = async (
  inputJson: XMLReport,
  bruksenhetsIds: string[],
  groupName: string
) => {
  const response = await api.post<FlerboligResponse>('registrering/ekspertbygg/flerboligbygg', {
    EkspertRegistrering: JSON.stringify(inputJson),
    BruksenhetsIder: bruksenhetsIds,
    Registreringsnavn: groupName
  });

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not create flerbolig registrations');
  }

  return response.data;
};

export const createFlerboligRegistrationsJson = async (
  inputJson: object,
  bruksenhetsIds: string[],
  groupName: string
) => {
  const response = await api.post<FlerboligResponse>('registrering/ekspertbygg/flerboligbygg/json', {
    EkspertRegistrering: JSON.stringify(inputJson),
    BruksenhetsIder: bruksenhetsIds,
    Registreringsnavn: groupName
  });

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not create flerbolig registrations from JSON');
  }

  return response.data;
};

export const createPortefolje = async (portefolje: CreatePortefoljeDTO) => {
  const response = await api.post<Portefolje>('portefolje', portefolje);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not create portefolje');
  }

  return response.data;
};

export const createTiltakEnergiplan = async (tiltak: Tiltak) => {
  const response = await api.post<Tiltak>('tiltak', tiltak);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not create tiltak for energiplan');
  }

  return response.data;
};

export const createTiltakPortefolje = async (portefoljeId: string | undefined, tiltak: Tiltak) => {
  if (!portefoljeId) {
    throw new Error('Cant add tiltak without a portefolje id');
  }

  const response = await api.post<Tiltak>(`tiltak/portefolje/${portefoljeId}`, tiltak);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not create tiltak for portefolje');
  }

  return response.data;
};

export const createTiltakGrupperegistrering = async (tiltak: Tiltak) => {
  const response = await api.post<Tiltak>('tiltak/grupperegistrering', tiltak);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not create tiltak for grupperegistrering');
  }

  return response.data;
};

export const updateTiltakGrupperegistrering = async (tiltak: Partial<Tiltak>) => {
  if (!tiltak.tiltakId) {
    throw new Error('Can not update tiltak without tiltakId');
  }
  const response = await api.put<Tiltak>(`tiltak/${tiltak.tiltakId}/grupperegistrering/`, tiltak);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not update tiltak for grupperegistrering');
  }

  return response.data;
};

export const getTiltakGrupperegistrering = async (grupperegistreringId?: string) => {
  if (!grupperegistreringId) {
    throw new Error('Can not get tiltak without grupperegistreringId');
  }

  const response = await api.get<Tiltak[]>(`tiltak/grupperegistrering/${grupperegistreringId}`);
  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve tiltak for grupperegistrering');
  }

  return response.data;
};

export const createExpertReport = async (report: ExpertReport) => {
  const response = await api.post<ExpertReport>('ekspertrapport', report);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not create expert report');
  }

  const data = response.data;

  if (!data) {
    throw new Error('Missing response data when creating expert report');
  }

  return data;
};

export const addReportsToPortefolje = (
  portefoljeId: string,
  content: XMLReport[]
): Promise<ApiResponse<XMLResponse[]>> =>
  // Timeout set to 0 to avoid timeout error when uploading multiple files
  api.post(`registrering/ekspertbygg/portefolje/${portefoljeId}`, content, {
    timeout: 0
  });

/**
 * Submits the UserInput object of a given registration, and calculates the new energy rating based on UserInput values
 * @param {string} registrationId The ID of the registration to be submitted
 * @param {UserInput} userInput UserInput object to be submitted
 * @returns {Promise<ApiResponse<SubmitRegistrationDTO>>} Promise object with EnergyPlan, UserInput and
 * UserInputErrorInformation[]
 */
export const submitRegistration = async (registrationId: string | undefined, userInput: UserInput) => {
  if (!registrationId) {
    throw new Error('Need to provide a registrationId when submitting a registration');
  }

  const response = await api.post<SubmitRegistrationDTO>(`energiplan/beregn/energimerke/${registrationId}`, userInput);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not submit registration');
  }

  return response.data;
};

export const publishRegistration = async (registrationId: string | undefined) => {
  if (!registrationId) {
    throw new Error('Missing registrationId when publishing registration');
  }

  const response = await api.post<PublishRegistrationDTO>(`energiplan/publish/${registrationId}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not publish registration');
  }

  return response.data;
};

export const publishFlerboligRegistration = async (flerboligbyggId?: string) => {
  if (!flerboligbyggId) {
    throw new Error('Missing flerboligId when publishing registration');
  }

  const response = await api.post(`energiplan/publish/flerboligbygg/${flerboligbyggId}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not publish flerbolig registration');
  }

  return response.data;
};

export const publishPortefolje = (portefoljeId: string): Promise<ApiResponse<PublishRegistrationDTO>> =>
  api.post(`portefolje/publish/${portefoljeId}`);

export const addBuildingToUserSelection = (energiObjektId: string): Promise<ApiResponse<Property>> =>
  api.post(`bruker/valgtebygninger/${energiObjektId}`);

export const getTiltaksKategorier = async (type: EnergiObjektType) => {
  const response = await api.get<Tiltakskategori[]>('tiltak/kategorier/load', {
    type
  });

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not retrieve tiltak categories');
  }

  const data = response.data;

  if (!data) {
    throw new Error('Missing measures category data');
  }

  return data;
};

// DELETE
export const deleteGrupperegistrering = async (grupperegistreringId: string) => {
  const response = await api.delete(`registrering/grupperegistrering/${grupperegistreringId}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not delete grupperegistrering');
  }

  return response.data;
};

export const deleteRegistration = async (registrationId: string) => {
  const response = await api.delete<Registration>(`registrering/${registrationId}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not delete registration');
  }

  return response.data;
};

export const deleteBuildingFromUserSelection = async (energiObjektId: string) => {
  const response = await api.delete<Property>(`bruker/valgtebygninger/${energiObjektId}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not remove building from selection');
  }

  return response.data;
};

export const deleteSoknad = async (soknadId: string | undefined) => {
  if (!soknadId) {
    throw new Error('Cant delete soknad when no soknadId is provided');
  }

  const response = await api.delete<Registration>(`registrering/soknad/${soknadId}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not delete soknad');
  }

  return response.data;
};

export const deleteTiltakEnergiplan = async (tiltakId?: string) => {
  if (!tiltakId) {
    throw new Error('Cant delete tiltak when no tiltakId is provided');
  }
  const response = await api.delete(`tiltak/${tiltakId}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not delete tiltak from energiplan');
  }

  return response.data;
};

export const deleteTiltakPortefolje = async (portefoljeTiltakId?: string, portefoljeId?: string) => {
  if (!portefoljeTiltakId) {
    throw new Error('Cant delete tiltak when missing tiltakId');
  }

  if (!portefoljeId) {
    throw new Error('Cant delete tiltak when missing portefoljeId');
  }

  const response = await api.delete(`tiltak/${portefoljeTiltakId}/portefolje/${portefoljeId}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not delete tiltak from portefolje');
  }

  return response.data;
};

export const deletePortefolje = async (portefoljeId: string) => {
  const response = await api.delete(`portefolje/${portefoljeId}`);

  if (!response.ok) {
    throw createApiRequestError(response, 'Could not delete portefolje');
  }

  return response.data;
};
